import { useState, useEffect } from "react";
import { useDealerRatings, Rating } from "@/hooks/useDealerRatings";
import { isSSR } from "@/lib/isSSR";

/**
 * Hook to get dealer ratings with SSR hydration support
 * This hook will use the ratings data embedded in the HTML during server-side rendering
 * and then hydrate with client-side data once available
 */
export function useHydratedDealerRatings(dealerId: string) {
  const [initialRatings, setInitialRatings] = useState<Rating[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);
  
  // Get client-side ratings data
  const { ratings, isLoading } = useDealerRatings(dealerId);
  
  // Extract ratings from embedded HTML on first render
  useEffect(() => {
    if (isSSR() || isHydrated) return;
    
    try {
      // Try to get ratings from the embedded data
      const dataScript = document.getElementById('__DEALER_DATA__');
      if (dataScript) {
        const data = JSON.parse(dataScript.textContent || '{}');
        const embeddedRatings = data.ratings?.[dealerId] || [];
        
        if (embeddedRatings.length > 0) {
          console.log(`[useHydratedDealerRatings] Found ${embeddedRatings.length} embedded ratings for dealer ${dealerId}`);
          setInitialRatings(embeddedRatings);
        }
      }
    } catch (err) {
      console.error('[useHydratedDealerRatings] Error extracting embedded ratings:', err);
    }
    
    setIsHydrated(true);
  }, [dealerId, isHydrated]);
  
  // Use embedded ratings until client-side data is loaded
  const displayRatings = isLoading && initialRatings.length > 0 
    ? initialRatings 
    : ratings;
  
  // Determine if we're still loading (only true if we don't have embedded data)
  const stillLoading = isLoading && initialRatings.length === 0;
  
  return {
    ratings: displayRatings,
    isLoading: stillLoading,
    isHydrated
  };
}
